// Core eCRF navigation: patient header, visit tabs, section tabs, and the
// section-pane dispatcher. Ported from renderECRF/renderVisitContent/
// renderSectionTabs/renderVisitOverview (source lines 3883-4459).
import { patientsApi, visitsApi, extraAdminsApi } from "./api-client.js";
import { $, $$, escapeHtml, calcAge, fmtDateOnly, notify } from "./dom.js";
import { STATE, canReadSite, canWriteSite } from "./state.js";
import { VISIT_CONFIG, getSectionsForVisit, EOS_SECTION, type SectionDef } from "@woof/shared";
import { openEditPatientModal, openNewPatientModal } from "./patients-view.js";
import { readFields, attachChangeHandler } from "./section-fields.js";
import { renderVitalSection, renderPhysicalSection, renderCaliperSection, renderCtscanSection, renderTnmSection, renderBloodSection, renderCytokinesSection, renderCorqSection } from "./visits-clinical.js";
import { renderEligibilitySection, renderInclusionSection, renderBiopsySection, renderMetastasisSection, renderPastTxSection, renderEosSection, renderDrugAdminSection } from "./visits-misc.js";
import { renderIrecistSection, attachIrecistHandlers } from "./visits-irecist.js";
import { renderMHSection, renderCMSection, attachMHHandlers, attachCMHandlers } from "./mh-cm-view.js";
import { renderAESection, attachAEHandlers } from "./ae-view.js";

const STATUS_MAP: Record<string, [string, string]> = {
  SCREENING: ["badge-info", "Screening"],
  ACTIVE: ["badge-success", "Actif"],
  COMPLETED: ["badge-info", "Terminé"],
  "WITHDRAWAL BY SUBJECT": ["badge-danger", "Sortie d'étude"],
  "SCREEN FAILURE": ["badge-warning", "Screen Failure"],
};

export async function renderECRF(root: HTMLElement): Promise<void> {
  if (!STATE.currentPatientId) {
    root.innerHTML = `<div class="empty-state"><div class="icon">📋</div><h3>Aucun patient sélectionné</h3><p>Sélectionnez un patient dans la barre latérale, ou créez un nouveau patient.</p><button class="btn btn-primary" id="btnEmptyNewPatient">➕ Nouveau patient</button></div>`;
    $("#btnEmptyNewPatient")!.addEventListener("click", () => openNewPatientModal());
    return;
  }

  let patient: any;
  try {
    patient = await patientsApi.get(STATE.currentPatientId);
  } catch {
    STATE.currentPatientId = null;
    void import("./router.js").then((m) => m.syncUrl(true));
    return renderECRF(root);
  }
  if (!canReadSite(patient.siteid)) {
    root.innerHTML = `<div class="empty-state"><h3>Accès refusé</h3><p>Vous n'avez pas accès à ce patient.</p></div>`;
    return;
  }
  const writable = canWriteSite(patient.siteid);
  const visits = await visitsApi.list(STATE.currentPatientId);
  const visitByKey = new Map(visits.map((v: any) => [v.visitKey, v]));
  const extraVisits = visits.filter((v: any) => v.isExtra).map((v: any) => v.visitKey).sort();

  if (!STATE.currentVisitKey) STATE.currentVisitKey = "V0";

  const [stCls, stLbl] = STATUS_MAP[patient.status] ?? ["badge-neutral", patient.status];

  root.innerHTML = `
    <div style="margin-bottom:16px">
      <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap">
        <h2 style="font-size:22px">${escapeHtml(patient.id)}</h2>
        <span class="badge ${stCls}">${stLbl}</span>
        <span style="color:var(--text-muted);font-size:13px">${escapeHtml(patient.breed || "—")} · ${escapeHtml(patient.sex || "?")} · ${patient.brthdtc ? calcAge(patient.brthdtc) : "âge ?"}</span>
        <button class="btn btn-sm btn-secondary" id="btnExportPdf" style="margin-left:auto">📄 Export PDF</button>
        ${writable ? `<button class="btn btn-sm btn-secondary" id="btnEditPatient">✏️ Éditer</button>` : ""}
      </div>
    </div>
    <div class="visit-tabs" id="visitTabs">
      ${VISIT_CONFIG.map((v) => {
        const isActive = STATE.currentVisitKey === v.vk;
        const isDone = !!visitByKey.get(v.vk)?.visitDate;
        return `<button class="visit-tab ${isActive ? "active" : ""}" data-vk="${v.vk}"><div class="vk">${v.vk}${isDone ? " ✓" : ""}</div><div class="vlbl">${escapeHtml(v.day)}</div></button>`;
      }).join("")}
      ${extraVisits.map((evk: string) => {
        const isActive = STATE.currentVisitKey === evk;
        const evd = visitByKey.get(evk) as any;
        return `<button class="visit-tab ${isActive ? "active" : ""}" data-vk="${evk}" style="border-left:3px solid var(--primary)"><div class="vk">${evk}${evd?.visitDate ? " ✓" : ""}</div><div class="vlbl">${evd?.visitDate ? evd.visitDate.slice(5) : "—"}</div></button>`;
      }).join("")}
    </div>
    <div id="visitContent"></div>`;

  $("#btnExportPdf")!.addEventListener("click", async () => {
    const { exportPatientPDF } = await import("./pdf-export.js");
    await exportPatientPDF(patient.id);
  });
  if (writable) $("#btnEditPatient")!.addEventListener("click", () => openEditPatientModal(patient));
  $$(".visit-tab").forEach((t) => {
    t.addEventListener("click", () => {
      STATE.currentVisitKey = t.getAttribute("data-vk");
      STATE.currentSection = null;
      // renderVisitContent() below only replaces #visitContent, not the tab
      // bar itself - toggle the active class here so the clicked tab is
      // reflected immediately instead of staying stuck on whichever tab was
      // active at the last full renderECRF() pass.
      $$(".visit-tab").forEach((other) => other.classList.remove("active"));
      t.classList.add("active");
      void import("./router.js").then((m) => m.syncUrl(true));
      void renderVisitContent(patient, writable);
    });
  });

  await renderVisitContent(patient, writable);
}

async function renderVisitContent(patient: any, writable: boolean): Promise<void> {
  const vk = STATE.currentVisitKey!;
  const contentRoot = $("#visitContent")!;
  const visitMeta = VISIT_CONFIG.find((v) => v.vk === vk) ?? { vk, num: 0, label: "Visite supplémentaire", day: "—", window: "" };

  const { visit, sections: sectionsData, derived } = await visitsApi.get(patient.id, vk);
  const visitDate = visit?.visitDate ?? null;

  const headerHtml = `
    <div class="visit-header-bar">
      <div class="visit-header-title"><span class="visit-header-icon">📅</span><strong>Visite ${vk}</strong>
        <span class="visit-header-label">${escapeHtml(visitMeta.label)}</span>
        <span class="visit-header-day">${escapeHtml(visitMeta.day)} ${escapeHtml(visitMeta.window || "")}</span>
      </div>
      <div class="visit-header-date">
        <label class="visit-header-datelabel"><span>Date de visite :</span>
          <input type="date" id="visitDateInput" value="${visitDate || ""}" ${writable ? "" : "disabled"} class="input-date-compact">
        </label>
      </div>
    </div>`;

  if (writable) {
    contentRoot.innerHTML = headerHtml + `<div id="sectionArea"></div>`;
    $("#visitDateInput")!.addEventListener("change", async (e) => {
      await visitsApi.setDate(patient.id, vk, (e.target as HTMLInputElement).value || null);
      void renderECRF($("#contentArea")! as HTMLElement);
    });
  } else {
    contentRoot.innerHTML = headerHtml + `<div id="sectionArea"></div>`;
  }
  const sectionArea = $<HTMLElement>("#sectionArea")!;

  if (vk === "EoS") {
    sectionArea.innerHTML = wrapSection(EOS_SECTION.icon, EOS_SECTION.label, renderEosSection(sectionsData.eos || {}, writable));
    attachSaveHandlers(sectionArea, patient.id, vk, "eos", sectionsData.eos || {}, writable);
    return;
  }
  if (vk === "VS") {
    await renderExtraVisitsHub(sectionArea, patient, writable);
    return;
  }
  if (vk === "AS") {
    await renderExtraAdminsHub(sectionArea, patient, writable);
    return;
  }

  const sections = getSectionsForVisit(vk);
  const currentSid = STATE.currentSection || "overview";
  const tabsHtml = `<div class="section-tabs"><button class="section-tab ${currentSid === "overview" ? "active" : ""}" data-sid="overview"><span class="ico">📋</span><span class="lbl">Sommaire</span></button>${renderSectionTabsHtml(sections, currentSid)}</div>`;

  if (currentSid === "overview") {
    sectionArea.innerHTML = tabsHtml + renderOverview(vk, visitMeta, sections, sectionsData);
    $$(".overview-card", sectionArea).forEach((btn) => btn.addEventListener("click", () => selectSection(btn.getAttribute("data-sid"), patient, writable)));
  } else {
    const sec = sections.find((s) => s.id === currentSid);
    if (!sec) {
      STATE.currentSection = "overview";
      void import("./router.js").then((m) => m.syncUrl(true));
      return renderVisitContent(patient, writable);
    }
    sectionArea.innerHTML = tabsHtml + `<div class="section-pane">${await renderSectionPane(sec, patient, vk, sectionsData, derived, writable)}</div>`;
    attachSectionPaneHandlers(sectionArea, sec, patient, vk, sectionsData, derived, writable);
  }

  $$(".section-tab", sectionArea).forEach((btn) => {
    btn.addEventListener("click", () => selectSection(btn.getAttribute("data-sid"), patient, writable));
  });
}

function selectSection(sid: string | null, patient: any, writable: boolean): void {
  STATE.currentSection = sid;
  void import("./router.js").then((m) => m.syncUrl(true));
  void renderVisitContent(patient, writable);
}

function renderSectionTabsHtml(sections: SectionDef[], currentSid: string): string {
  return sections
    .map((s) => `<button class="section-tab ${currentSid === s.id ? "active" : ""}" data-sid="${s.id}"><span class="ico">${s.icon}</span><span class="lbl">${escapeHtml(s.label)}</span></button>`)
    .join("");
}

function renderOverview(vk: string, visitMeta: any, sections: SectionDef[], sectionsData: Record<string, any>): string {
  const NO_STATUS = new Set(["cm", "ae"]);
  const cards = sections
    .map((s) => {
      const hasData = sectionsData[s.id] && Object.keys(sectionsData[s.id]).length > 0;
      const showStatus = !NO_STATUS.has(s.id);
      const info = hasData ? { bg: "#ecfdf5", border: "#6ee7b7", text: "#065f46", label: "Complet", icon: "✓" } : { bg: "#f3f4f6", border: "#d1d5db", text: "#6b7280", label: "Vide", icon: "○" };
      return `<button class="overview-card" data-sid="${s.id}" style="background:${info.bg};border:1px solid ${info.border};color:${info.text}">
        <div class="overview-card-icon">${s.icon}</div>
        <div class="overview-card-body"><div class="overview-card-label">${escapeHtml(s.label)}</div>
        ${showStatus ? `<div class="overview-card-status">${info.icon} ${info.label}</div>` : `<div class="overview-card-status muted">Liste patient</div>`}</div>
      </button>`;
    })
    .join("");
  const trackable = sections.filter((s) => !NO_STATUS.has(s.id));
  const completed = trackable.filter((s) => sectionsData[s.id] && Object.keys(sectionsData[s.id]).length > 0).length;
  const progress = trackable.length > 0 ? Math.round((completed / trackable.length) * 100) : 0;
  return `<div class="visit-overview">
    <div class="overview-header">
      <h3 style="margin:0 0 6px 0">📋 Sommaire — Visite ${vk}</h3>
      <p class="muted" style="margin:0 0 10px 0">${escapeHtml(visitMeta.label)} · ${escapeHtml(visitMeta.day)}</p>
      <div class="overview-progress"><div class="overview-progress-bar"><div class="overview-progress-fill" style="width:${progress}%"></div></div>
      <span class="overview-progress-label"><strong>${completed} / ${trackable.length}</strong> sections complètes (${progress}%)</span></div>
    </div>
    <div class="overview-grid">${cards}</div>
  </div>`;
}

function wrapSection(icon: string, label: string, bodyHtml: string): string {
  return `<div class="section"><div class="section-header"><h4>${icon} ${escapeHtml(label)}</h4></div><div class="section-body">${bodyHtml}</div></div>`;
}

async function renderSectionPane(sec: SectionDef, patient: any, vk: string, sectionsData: Record<string, any>, derived: any, writable: boolean): Promise<string> {
  const data = sectionsData[sec.id] || {};
  switch (sec.id) {
    case "eligibility": return wrapSection(sec.icon, sec.label, renderEligibilitySection(data, writable));
    case "vital": return wrapSection(sec.icon, sec.label, renderVitalSection(data, writable));
    case "physical": return wrapSection(sec.icon, sec.label, renderPhysicalSection(data, writable));
    case "caliper": return wrapSection(sec.icon, sec.label, renderCaliperSection(data, writable));
    case "ctscan": return wrapSection(sec.icon, sec.label, renderCtscanSection(data, writable));
    case "tnm": return wrapSection(sec.icon, sec.label, renderTnmSection(data, sectionsData.caliper || {}, writable));
    case "biopsy": return wrapSection(sec.icon, sec.label, renderBiopsySection(data, writable));
    case "metastasis": return wrapSection(sec.icon, sec.label, renderMetastasisSection(data, writable));
    case "past_tx": return wrapSection(sec.icon, sec.label, renderPastTxSection(data, writable));
    case "blood": return wrapSection(sec.icon, sec.label, renderBloodSection(data, writable));
    case "cytokines": return wrapSection(sec.icon, sec.label, renderCytokinesSection(data, writable));
    case "corq": return wrapSection(sec.icon, sec.label, renderCorqSection(data, writable));
    case "inclusion": return wrapSection(sec.icon, sec.label, renderInclusionSection(data, sectionsData.eligibility || {}, writable));
    case "drug_admin": return wrapSection(sec.icon, sec.label, renderDrugAdminSection(data, derived, patient.id, writable));
    case "irecist": {
      const v1 = vk === "V1" ? data : (await visitsApi.get(patient.id, "V1")).sections.irecist || {};
      return wrapSection(sec.icon, sec.label, renderIrecistSection(vk, data, v1, derived));
    }
    case "mh": return renderMHSection(patient.id, writable);
    case "cm": return renderCMSection(patient.id, writable);
    case "ae": return renderAESection(patient.id, writable, vk);
    default: return wrapSection(sec.icon, sec.label, `<p class="muted">Section non implémentée.</p>`);
  }
}

function attachSaveHandlers(root: ParentNode, pid: string, vk: string, sectionId: string, currentData: Record<string, any>, writable: boolean): void {
  if (!writable) return;
  attachChangeHandler(root, async () => {
    const patch = readFields(root);
    const merged = { ...currentData, ...patch };
    try {
      await visitsApi.putSection(pid, vk, sectionId, merged);
    } catch (err) {
      notify("error", err instanceof Error ? err.message : "Erreur d'enregistrement");
      return;
    }
    const patient = await patientsApi.get(pid);
    await renderVisitContent(patient, writable);
  });
}

function attachSectionPaneHandlers(root: ParentNode, sec: SectionDef, patient: any, vk: string, sectionsData: Record<string, any>, derived: any, writable: boolean): void {
  const data = sectionsData[sec.id] || {};
  if (sec.id === "mh") return attachMHHandlers(root, patient.id, () => void renderVisitContent(patient, writable));
  if (sec.id === "cm") return attachCMHandlers(root, patient.id, () => void renderVisitContent(patient, writable));
  if (sec.id === "ae") return attachAEHandlers(root, patient.id, vk, () => void renderVisitContent(patient, writable));
  if (sec.id === "irecist") {
    attachIrecistHandlers(root, data, derived?.irecist?.suggestion, async (patch) => {
      if (!writable) return;
      const merged = { ...data, ...patch };
      await visitsApi.putSection(patient.id, vk, "irecist", merged);
      await renderVisitContent(patient, writable);
    });
  }
  attachSaveHandlers(root, patient.id, vk, sec.id, data, writable);
}

async function renderExtraVisitsHub(root: HTMLElement, patient: any, writable: boolean): Promise<void> {
  const visits = await visitsApi.list(patient.id);
  const extra = visits.filter((v: any) => v.isExtra);
  root.innerHTML = `
    <div class="section"><div class="section-header"><h4>📁 Visites supplémentaires</h4></div>
    <div class="section-body">
      ${writable ? `<button class="btn btn-primary btn-sm" id="btnCreateExtraVisit">+ Créer une visite supplémentaire</button>` : ""}
      <table class="data-table" style="margin-top:10px"><thead><tr><th>Visite</th><th>Date</th></tr></thead>
      <tbody>${extra.length ? extra.map((v: any) => `<tr><td><a href="#" data-open-visit="${v.visitKey}">${v.visitKey}</a></td><td>${fmtDateOnly(v.visitDate)}</td></tr>`).join("") : `<tr><td colspan="2" class="muted">Aucune visite supplémentaire.</td></tr>`}</tbody>
      </table>
    </div></div>`;
  $("#btnCreateExtraVisit")?.addEventListener("click", async () => {
    const created = await visitsApi.createExtra(patient.id);
    const { navigate } = await import("./router.js");
    navigate({ view: "ecrf", currentVisitKey: created.visitKey, currentSection: null });
  });
  root.querySelectorAll<HTMLElement>("[data-open-visit]").forEach((a) => {
    a.addEventListener("click", async (e) => {
      e.preventDefault();
      STATE.currentVisitKey = a.getAttribute("data-open-visit");
      STATE.currentSection = null;
      const { syncUrl } = await import("./router.js");
      syncUrl(true);
      await renderECRF($("#contentArea") as HTMLElement);
    });
  });
}

async function renderExtraAdminsHub(root: HTMLElement, patient: any, writable: boolean): Promise<void> {
  const list = await extraAdminsApi.list(patient.id);
  root.innerHTML = `
    <div class="section"><div class="section-header"><h4>💉 Administrations supplémentaires</h4></div>
    <div class="section-body">
      ${writable ? `<button class="btn btn-primary btn-sm" id="btnAddExtraAdmin">+ Ajouter une administration</button>` : ""}
      <table class="data-table" style="margin-top:10px"><thead><tr><th>Date</th><th>Volume (mL)</th><th>Site</th><th>Opérateur</th></tr></thead>
      <tbody>${list.length ? list.map((a: any) => `<tr><td>${fmtDateOnly(a.exstdat)}</td><td>${a.exdose ?? "—"}</td><td>${escapeHtml(a.exloc || "")}</td><td>${escapeHtml(a.operator || "")}</td></tr>`).join("") : `<tr><td colspan="4" class="muted">Aucune administration supplémentaire.</td></tr>`}</tbody>
      </table>
    </div></div>`;
  $("#btnAddExtraAdmin")?.addEventListener("click", async () => {
    const date = prompt("Date d'administration (YYYY-MM-DD) :");
    if (!date) return;
    const exdose = Number(prompt("Volume injecté (mL) :") || "0");
    await extraAdminsApi.create(patient.id, { exstdat: date, exdose });
    await renderExtraAdminsHub(root, patient, writable);
  });
}
